import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { facilitiesApi } from "./api";
import { useFacilityRole } from "./useFacilityRole";
import { reportsApi } from "@/features/reports/api";
import { SlaBadge } from "@/features/reports/SlaBadge";
import { tasksApi, insightsApi } from "@/features/tasks/api";
import type { Item, ItemStatus } from "./types";
import type { Report } from "@/features/reports/types";
import type { MaintenanceTask, Insight } from "@/features/tasks/types";

const STATUS_TONE: Record<ItemStatus, string> = {
  ok: "bg-emerald-100 text-emerald-700",
  minor: "bg-amber-100 text-amber-700",
  major: "bg-orange-100 text-orange-700",
  out_of_service: "bg-rose-100 text-rose-700",
  unknown: "bg-slate-100 text-slate-600",
};

const REPORT_TONE: Record<string, string> = {
  submitted: "bg-blue-100 text-blue-700",
  triaged: "bg-cyan-100 text-cyan-700",
  acknowledged: "bg-amber-100 text-amber-700",
  in_progress: "bg-orange-100 text-orange-700",
  resolved: "bg-emerald-100 text-emerald-700",
  duplicate: "bg-slate-100 text-slate-600",
  dismissed: "bg-slate-100 text-slate-500",
};

export function OverviewPage() {
  const { id } = useParams<{ id: string }>();
  const facilityId = Number(id);

  const itemsQ = useQuery({
    queryKey: ["items", facilityId],
    queryFn: () => facilitiesApi.listItems(facilityId),
    enabled: !Number.isNaN(facilityId),
  });
  const submittedQ = useQuery({
    queryKey: ["reports", facilityId, "submitted"],
    queryFn: () =>
      reportsApi.list({ facility: facilityId, status: "submitted" }),
    enabled: !Number.isNaN(facilityId),
  });
  const ackQ = useQuery({
    queryKey: ["reports", facilityId, "acknowledged"],
    queryFn: () =>
      reportsApi.list({ facility: facilityId, status: "acknowledged" }),
    enabled: !Number.isNaN(facilityId),
  });
  const inProgressQ = useQuery({
    queryKey: ["reports", facilityId, "in_progress"],
    queryFn: () =>
      reportsApi.list({ facility: facilityId, status: "in_progress" }),
    enabled: !Number.isNaN(facilityId),
  });
  const activeTasksQ = useQuery({
    queryKey: ["tasks", facilityId, "active"],
    queryFn: () => tasksApi.list(facilityId, "active"),
    enabled: !Number.isNaN(facilityId),
  });
  const proposedQ = useQuery({
    queryKey: ["tasks", facilityId, "proposed"],
    queryFn: () => tasksApi.list(facilityId, "proposed"),
    enabled: !Number.isNaN(facilityId),
  });
  const insightsQ = useQuery({
    queryKey: ["insights", facilityId],
    queryFn: () => insightsApi.list(facilityId),
    enabled: !Number.isNaN(facilityId),
  });

  const items = itemsQ.data?.results ?? [];
  const allOpenReports: Report[] = [
    ...(submittedQ.data?.results ?? []),
    ...(ackQ.data?.results ?? []),
    ...(inProgressQ.data?.results ?? []),
  ].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  const itemsNeedingAttention = items.filter((i) =>
    ["major", "out_of_service"].includes(i.current_status),
  );
  const dueSoon = (activeTasksQ.data?.results ?? [])
    .filter((t) => t.next_due_at != null)
    .sort(
      (a, b) =>
        new Date(a.next_due_at!).getTime() -
        new Date(b.next_due_at!).getTime(),
    );
  const { isOwner } = useFacilityRole(facilityId);
  const insights = insightsQ.data?.results ?? [];
  const proposedCount = proposedQ.data?.count ?? 0;
  const isEmptyFacility =
    itemsQ.isFetched && items.length === 0;

  return (
    <div className="space-y-6">
      {isEmptyFacility && isOwner && (
        <WalkOnboardingBanner facilityId={facilityId} />
      )}
      <div className="grid gap-4 lg:grid-cols-3">
        <NeedsAttention
          items={itemsNeedingAttention}
          reports={allOpenReports.slice(0, 4)}
          facilityId={facilityId}
        />
        {/* AI suggestions are an owner review queue (proposed tasks +
            pattern flags). Hide for maintainers — they don't accept or
            dismiss these. */}
        {isOwner && (
          <AISuggestions
            proposedCount={proposedCount}
            insights={insights}
            facilityId={facilityId}
          />
        )}
      </div>

      <DueThisWeek tasks={dueSoon} facilityId={facilityId} />
    </div>
  );
}

function WalkOnboardingBanner({ facilityId }: { facilityId: number }) {
  const { t } = useTranslation();
  return (
    <section className="rounded-lg border border-brand-500/30 bg-gradient-to-r from-brand-50 to-white p-5">
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="max-w-xl">
          <p className="text-sm font-semibold text-slate-900">
            {t("items.empty_cta_walk")}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            {t("items.empty_cta_walk_blurb")}
          </p>
        </div>
        <Link
          to={`/facilities/${facilityId}/walk`}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-500"
        >
          {t("items.empty_cta_walk")}
        </Link>
      </div>
    </section>
  );
}

function NeedsAttention({
  items,
  reports,
  facilityId,
}: {
  items: Item[];
  reports: Report[];
  facilityId: number;
}) {
  const { t } = useTranslation();
  const empty = items.length === 0 && reports.length === 0;
  return (
    <section className="rounded-lg border border-slate-200 bg-white lg:col-span-2">
      <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-rose-500"></span>
          <h2 className="text-sm font-semibold">{t("overview.needs_attention")}</h2>
        </div>
        <span className="text-xs text-slate-500">
          {t("overview.items_reports", {
            items: items.length,
            reports: reports.length,
          })}
        </span>
      </header>
      {empty ? (
        <p className="px-5 py-6 text-sm text-slate-500">
          {t("overview.all_clear")}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((it) => (
            <li
              key={`item-${it.id}`}
              className="flex items-center justify-between gap-3 px-5 py-3"
            >
              <p className="text-sm font-medium">{it.name}</p>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[it.current_status]}`}
              >
                {t(`items.status.${it.current_status}` as const)}
              </span>
            </li>
          ))}
          {reports.map((r) => (
            <li
              key={`report-${r.id}`}
              className="flex items-center justify-between gap-3 px-5 py-3"
            >
              <Link
                to={`/facilities/${facilityId}/reports/${r.id}`}
                className="min-w-0 flex-1"
              >
                <p className="truncate text-sm font-medium">
                  {r.title || t("reports.no_title")}
                </p>
                <p className="truncate text-xs text-slate-500">{r.body}</p>
              </Link>
              <div className="flex flex-col items-end gap-1">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${REPORT_TONE[r.status]}`}
                >
                  {t(`reports.status.${r.status}` as const)}
                </span>
                <SlaBadge breachAt={r.sla_breach_at} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AISuggestions({
  proposedCount,
  insights,
  facilityId,
}: {
  proposedCount: number;
  insights: Insight[];
  facilityId: number;
}) {
  const { t } = useTranslation();
  const empty = proposedCount === 0 && insights.length === 0;
  return (
    <section className="rounded-lg border border-brand-500/30 bg-gradient-to-b from-brand-50 to-white">
      <header className="flex items-center justify-between border-b border-brand-500/20 px-5 py-3">
        <h2 className="text-sm font-semibold">{t("overview.ai_suggestions")}</h2>
        {proposedCount > 0 && (
          <Link
            to={`/facilities/${facilityId}/tasks/proposed`}
            className="text-xs font-medium text-brand-600 hover:underline"
          >
            {t("overview.review")}
          </Link>
        )}
      </header>
      {empty ? (
        <p className="px-5 py-6 text-sm text-slate-500">
          {t("overview.nothing_new")}
        </p>
      ) : (
        <ul className="space-y-1 p-3 text-sm">
          {proposedCount > 0 && (
            <li className="rounded-md p-2">
              <p className="font-medium">
                {t(
                  proposedCount === 1
                    ? "overview.proposed_tasks_one"
                    : "overview.proposed_tasks_other",
                  { count: proposedCount },
                )}
              </p>
              <p className="text-xs text-slate-500">
                {t("overview.awaiting_review")}
              </p>
            </li>
          )}
          {insights.length > 0 && (
            <li className="space-y-1 rounded-md p-2">
              <p className="font-medium">
                {t(
                  insights.length === 1
                    ? "overview.pattern_flags_one"
                    : "overview.pattern_flags_other",
                  { count: insights.length },
                )}
              </p>
              <ul className="space-y-1 text-xs text-slate-600">
                {insights.slice(0, 3).map((i) => (
                  <InsightRow
                    key={i.id}
                    insight={i}
                    facilityId={facilityId}
                  />
                ))}
              </ul>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

function InsightRow({
  insight,
  facilityId,
}: {
  insight: Insight;
  facilityId: number;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => insightsApi.dismiss(insight.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["insights", facilityId] }),
  });
  const summary = String(
    insight.payload.pattern ?? insight.payload.summary ?? insight.kind,
  );
  return (
    <li className="flex items-center justify-between gap-2">
      <span className="min-w-0 flex-1 truncate">{summary}</span>
      <button
        type="button"
        onClick={() => m.mutate()}
        disabled={m.isPending}
        className="text-[11px] font-medium text-slate-500 hover:text-slate-900"
      >
        {t("overview.dismiss")}
      </button>
    </li>
  );
}

function DueThisWeek({
  tasks,
  facilityId,
}: {
  tasks: MaintenanceTask[];
  facilityId: number;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const now = Date.now();
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <h2 className="text-sm font-semibold">{t("overview.due_soon")}</h2>
        <Link
          to={`/facilities/${facilityId}/tasks`}
          className="text-xs font-medium text-brand-600 hover:underline"
        >
          {t("overview.all_tasks")}
        </Link>
      </header>
      {tasks.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-500">
          {t("overview.no_upcoming")}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {tasks.slice(0, 6).map((task) => {
            const dueIn = task.next_due_at
              ? Math.round(
                  (new Date(task.next_due_at).getTime() - now) /
                    (1000 * 60 * 60 * 24),
                )
              : null;
            const tone =
              dueIn != null && dueIn < 0
                ? "text-rose-600"
                : dueIn != null && dueIn <= 7
                  ? "text-amber-600"
                  : "text-slate-500";
            return (
              <li
                key={task.id}
                className="flex items-center justify-between gap-3 px-5 py-3"
              >
                <Link
                  to={`/facilities/${facilityId}/tasks/${task.id}`}
                  className="min-w-0 flex-1"
                >
                  <p className="text-sm font-medium">{task.name}</p>
                  <p className="text-xs text-slate-500">
                    {cadenceLabel(task, t, locale)}
                  </p>
                </Link>
                <span className={`text-xs font-medium ${tone}`}>
                  {dueIn == null
                    ? t("overview.no_due_date")
                    : dueIn < 0
                      ? t("overview.overdue_by", { count: -dueIn })
                      : dueIn === 0
                        ? t("overview.today")
                        : t("overview.in_n_days", { count: dueIn })}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function cadenceLabel(
  task: MaintenanceTask,
  t: TFunction,
  locale: string,
): string {
  if (task.cadence_kind === "interval_days" && task.interval_days)
    return t("tasks.cadence.every_n_days", { count: task.interval_days });
  if (task.cadence_kind === "annual_month" && task.annual_month) {
    const month = new Date(2000, task.annual_month - 1).toLocaleString(locale, {
      month: "long",
    });
    return t("tasks.cadence.annually", { month });
  }
  if (task.cadence_kind === "seasonal" && task.season)
    return t("tasks.cadence.seasonal", { season: task.season });
  if (task.cadence_kind === "one_off") return t("tasks.cadence.one_off");
  return task.cadence_kind;
}
